import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const LIVE_REGION_CLEAR_MS = 5000;

/**
 * @param {HTMLElement} root
 * @returns {{ id: number, quantity: number }[]}
 */
function collectSelectedFbtLineItems(root) {
  /** @type {{ id: number, quantity: number }[]} */
  const items = [];
  const seen = new Set();
  for (const row of root.querySelectorAll('[data-fbt-item]')) {
    const checkbox = row.querySelector('[data-fbt-checkbox]');
    if (!checkbox?.checked) continue;
    const id = Number(row.dataset.fbtVariantId);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    items.push({ id, quantity: 1 });
  }
  return items;
}

/**
 * @param {HTMLElement} root
 * @param {string} variantId
 * @param {boolean} disabled
 */
function setFbtMediaCellDisabled(root, variantId, disabled) {
  const safe =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(String(variantId))
      : String(variantId);
  const cell = root.querySelector(`[data-fbt-media-for="${safe}"]`);
  cell?.classList.toggle('fbt__media-cell--disabled', disabled);
}

function updateFrequentlyBoughtTogether(root) {
  const moneyFormat = root.dataset.moneyFormat || '{{amount}}';
  const currency = root.dataset.currency || 'USD';

  let saleSum = 0;
  let compareSum = 0;
  let checkedCount = 0;

  const items = root.querySelectorAll('[data-fbt-item]');

  for (const item of items) {
    const checkbox = item.querySelector('[data-fbt-checkbox]');
    if (!checkbox) continue;

    const isChecked = checkbox.checked;
    const variantId = item.dataset.fbtVariantId || '';
    setFbtMediaCellDisabled(root, variantId, !isChecked);

    if (!isChecked) continue;

    checkedCount++;
    saleSum += Number(item.dataset.saleCents || 0);
    compareSum += Number(item.dataset.compareCents || item.dataset.saleCents || 0);
  }

  const addButton = root.querySelector('[data-fbt-add-button]');
  if (addButton instanceof HTMLButtonElement) {
    addButton.disabled = checkedCount === 0;
  }

  const saleEl = root.querySelector('[data-fbt-total="sale"]');
  const compareRow = root.querySelector('[data-fbt-compare-row]');
  const compareEl = root.querySelector('[data-fbt-total="compare"]');
  const savingsEl = root.querySelector('[data-fbt-savings]');
  const savingsTextEl = root.querySelector('[data-fbt-savings-text]');

  if (saleEl) {
    saleEl.textContent = formatMoney(saleSum, moneyFormat, currency);
  }

  const showCompare = compareSum > saleSum;
  const savingsAmount = showCompare ? compareSum - saleSum : 0;

  if (compareRow && compareEl) {
    compareEl.textContent = formatMoney(compareSum, moneyFormat, currency);
    compareRow.hidden = !showCompare;
  }

  if (savingsEl && savingsTextEl) {
    if (savingsAmount > 0) {
      savingsTextEl.textContent = formatMoney(savingsAmount, moneyFormat, currency);
      savingsEl.hidden = false;
    } else {
      savingsTextEl.textContent = '';
      savingsEl.hidden = true;
    }
  }
}

/**
 * @param {HTMLElement} root
 */
async function submitFbtUpsellsOnly(root) {
  const lineItems = collectSelectedFbtLineItems(root);
  if (lineItems.length === 0) return;

  const button = root.querySelector('[data-fbt-add-button]');
  if (!(button instanceof HTMLButtonElement)) return;

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  for (const el of cartItemsComponents) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  }

  button.disabled = true;

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: lineItems,
        sections: sectionIds.join(','),
      }),
    });

    const data = await response.json();

    if (data.status) {
      document.dispatchEvent(
        new CartErrorEvent('frequently-bought-together', data.message, data.description, data.errors)
      );
      return;
    }

    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();

    document.dispatchEvent(
      new CartAddEvent(cart, 'frequently-bought-together', {
        source: 'frequently-bought-together',
        itemCount: lineItems.reduce((sum, item) => sum + item.quantity, 0),
        sections: data.sections,
      })
    );

    const live = root.querySelector('[data-fbt-live-region]');
    if (live) {
      const announcement = Theme.translations?.added || '';
      live.textContent = announcement;
      setTimeout(() => {
        live.textContent = '';
      }, LIVE_REGION_CLEAR_MS);
    }
  } catch (error) {
    console.error(error);
  } finally {
    updateFrequentlyBoughtTogether(root);
  }
}

/**
 * @param {HTMLElement} root
 */
function initFrequentlyBoughtTogether(root) {
  updateFrequentlyBoughtTogether(root);

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.matches('[data-fbt-checkbox]')) {
      updateFrequentlyBoughtTogether(root);
    }
  });

  const addButton = root.querySelector('[data-fbt-add-button]');
  addButton?.addEventListener('click', () => {
    submitFbtUpsellsOnly(root);
  });
}

document.querySelectorAll('[data-fbt-root]').forEach(initFrequentlyBoughtTogether);
